import { useEffect, useRef, useState } from 'react'
import { FilesClient } from '@/lib/files'

/**
 * The project's own Claude Code commands, skills and subagents: files in the
 * repo's `.claude` folder, so they travel with git to every session and
 * every clone, and an edit here is in the next commit like any other change.
 */
export type KitKind = 'command' | 'skill' | 'agent'
export type KitItem = { kind: KitKind; name: string; description: string; path: string; text: string; mtime: number }

export const KIT_DIRS: Record<KitKind, string> = { command: '.claude/commands', skill: '.claude/skills', agent: '.claude/agents' }
export const KIT_NAME = /^[a-z0-9][a-z0-9-]{0,63}$/

export const kitPath = (kind: KitKind, name: string) =>
  kind === 'skill' ? `${KIT_DIRS.skill}/${name}/SKILL.md` : `${KIT_DIRS[kind]}/${name}.md`

/** `key: value` lines between the leading `---` fences; enough for name and description. */
export function frontmatter(text: string): Record<string, string> {
  const m = /^---\r?\n([\s\S]*?)\r?\n---/.exec(text)
  const out: Record<string, string> = {}
  const lines = m?.[1]?.split(/\r?\n/) ?? []
  for (let i = 0; i < lines.length; i++) {
    const kv = /^([A-Za-z_-]+):\s*(.*)$/.exec(lines[i]!)
    if (!kv?.[1]) continue
    const v = (kv[2] ?? '').trim()
    // `description: >` (or `|`) puts the text on the indented lines below; many skills do.
    if (/^[>|][-+]?$/.test(v)) {
      const block: string[] = []
      while (i + 1 < lines.length && (/^\s+\S/.test(lines[i + 1]!) || !lines[i + 1]!.trim())) block.push(lines[++i]!.trim())
      out[kv[1]] = (v.startsWith('|') ? block.join('\n') : block.join(' ').replace(/\s+/g, ' ')).trim()
    } else out[kv[1]] = v.replace(/^["']|["']$/g, '').trim()
  }
  return out
}

/** The file without its frontmatter: what the command or skill tells Claude to do. */
export const body = (text: string) => text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '').trim()

export function template(kind: KitKind, name: string): string {
  if (kind === 'command') return `---\ndescription: What /${name} does\nargument-hint: [what to type after it]\n---\n\nWhat Claude should do. $ARGUMENTS is whatever follows /${name}.\n`
  if (kind === 'skill') return `---\nname: ${name}\ndescription: When Claude should use this skill, in one sentence\n---\n\n# ${name}\n\nThe steps Claude follows.\n`
  return `---\nname: ${name}\ndescription: When to hand work to this subagent\ntools: Read, Grep, Glob, Bash\nmodel: inherit\n---\n\nYou are a … Your job is …\n`
}

/** Claude Code's own commands that make sense from the chat: they run or print and return. */
export const BUILTIN_COMMANDS: { name: string; description: string; hint?: string }[] = [
  { name: 'compact', description: 'Summarise the conversation so far to free up context', hint: '[focus]' },
  { name: 'clear', description: 'Start the conversation over, keeping the session' },
  { name: 'context', description: 'Show what is using the context window' },
  { name: 'cost', description: 'Show this conversation’s token use' },
  { name: 'model', description: 'Change the model', hint: '<model>' },
  { name: 'effort', description: 'Change how hard Claude thinks', hint: '<low|medium|high|xhigh|max>' },
  { name: 'init', description: 'Write a CLAUDE.md describing this codebase' },
  { name: 'review', description: 'Review a pull request', hint: '[pr]' },
  { name: 'security-review', description: 'Security review of the pending changes' },
  { name: 'pr-comments', description: 'Fetch the comments on a pull request', hint: '[pr]' },
  { name: 'mcp', description: 'Show the MCP servers and their state' },
  { name: 'memory', description: 'Show the memory files Claude loaded' },
  { name: 'agents', description: 'List the subagents' },
  { name: 'status', description: 'Show version, model, account and connectivity' },
  { name: 'help', description: 'List every command' },
]

type Watcher = { client: FilesClient | null }

/**
 * Watches the three folders (and each skill's folder) in the session's
 * checkout and keeps every file's text; edits from Claude or the terminal
 * show up too, since listings arrive whenever a folder changes.
 */
export function useClaudeKit(sessionId: string | undefined, mode: 'ro' | 'rw') {
  const [items, setItems] = useState<Record<string, KitItem>>({})
  const [writable, setWritable] = useState(false)
  const w = useRef<Watcher>({ client: null })

  useEffect(() => {
    if (!sessionId) return
    setItems({})
    const fetched = new Map<string, number>()
    const skillDirs = new Set<string>()
    const load = (c: FilesClient, kind: KitKind, name: string, path: string, mtime: number) => {
      if (fetched.get(path) === mtime) return
      fetched.set(path, mtime)
      c.download(path).then((b) => b.text()).then((text) => {
        setItems((i) => ({ ...i, [path]: { kind, name, path, text, mtime, description: frontmatter(text).description ?? '' } }))
      }).catch(() => fetched.delete(path))
    }
    const drop = (keep: (it: KitItem) => boolean) => setItems((i) => {
      const next = Object.fromEntries(Object.entries(i).filter(([, it]) => keep(it)))
      return Object.keys(next).length === Object.keys(i).length ? i : next
    })
    const c = new FilesClient(sessionId, mode, {
      ready: (r) => {
        setWritable(r.writable)
        for (const d of Object.values(KIT_DIRS)) c.watch(d)
        for (const d of skillDirs) c.watch(d)
      },
      listing: (path, entries) => {
        const ents = entries ?? []
        for (const kind of ['command', 'agent'] as const) {
          if (path !== KIT_DIRS[kind]) continue
          const md = ents.filter((e) => !e.dir && e.name.endsWith('.md'))
          const names = new Set(md.map((e) => `${path}/${e.name}`))
          drop((it) => it.kind !== kind || names.has(it.path))
          for (const e of md) load(c, kind, e.name.slice(0, -3), `${path}/${e.name}`, e.mtime_ms)
        }
        if (path === KIT_DIRS.skill) {
          const dirs = new Set(ents.filter((e) => e.dir).map((e) => `${path}/${e.name}`))
          for (const d of skillDirs) if (!dirs.has(d)) { skillDirs.delete(d); c.forget(d) }
          for (const d of dirs) if (!skillDirs.has(d)) { skillDirs.add(d); c.watch(d) }
          drop((it) => it.kind !== 'skill' || dirs.has(it.path.replace(/\/SKILL\.md$/, '')))
        } else if (path.startsWith(`${KIT_DIRS.skill}/`)) {
          const f = ents.find((e) => !e.dir && e.name === 'SKILL.md')
          const p = `${path}/SKILL.md`
          if (f) load(c, 'skill', path.slice(KIT_DIRS.skill.length + 1), p, f.mtime_ms)
          else drop((it) => it.path !== p)
        }
      },
      state: () => {},
    })
    w.current.client = c
    return () => { c.stop(); w.current.client = null }
  }, [sessionId, mode])

  /** Write a file, making its folders first; resolves once it is on disk. */
  const save = async (kind: KitKind, name: string, text: string, mtime?: number) => {
    const c = w.current.client
    if (!c) throw new Error('Not connected to the session')
    const path = kitPath(kind, name)
    const dirs = ['.claude', KIT_DIRS[kind], ...(kind === 'skill' ? [path.replace(/\/SKILL\.md$/, '')] : [])]
    // mkdir of a folder that is already there fails; that is fine.
    for (const d of dirs) await c.mkdir(d).catch(() => {})
    await c.save(path, text, mtime)
    // A folder that did not exist was not being watched into.
    c.watch(KIT_DIRS[kind])
    if (kind === 'skill') c.watch(path.replace(/\/SKILL\.md$/, ''))
  }
  const remove = async (it: KitItem) => {
    const c = w.current.client
    if (!c) throw new Error('Not connected to the session')
    await c.remove(it.kind === 'skill' ? it.path.replace(/\/SKILL\.md$/, '') : it.path)
  }

  const list = Object.values(items).sort((a, b) => a.name.localeCompare(b.name))
  return { items: list, writable: writable && mode === 'rw', save, remove }
}

export type ClaudeKit = ReturnType<typeof useClaudeKit>
